import React, { MouseEventHandler } from 'react';
import { useFormikContext } from 'formik';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { TextField } from '@elevator/ui';
import SectionHeader from '../SectionHeader/SectionHeader';
import ImageUploader from '../ImageUploader/ImageUploader';
import ActionButtons from '../ActionButtons/ActionButtons';
import CreatorsForm from 'organisms/CreatorsForm';
import AuthorsSelect from '../AuthorsSelect';
import { SubmissionContent } from 'types/submissions';
import { CreditInput } from 'types/credit';
import useStyles from './styles';

const MAX_DESCRIPTION_CHAR_COUNT = 5000;

export interface SubmissionFormProps {
  /** Credits list for CreditsAccordion */
  credits: CreditInput[];
  /** Called when a credit is removed */
  removeCredit: (creditId: string | undefined, index: number) => void | Promise<void>;
  /** Called when a credit is saved (created or updated). Must return Promise for CreditsAccordion. */
  saveCredit: (credit: CreditInput, index: number) => Promise<void>;
  /** Whether new credits can be added. Default true */
  canAddCredits?: boolean;
  /** Label for the credits section. Default "Credits" */
  creditsLabel?: string;
  /** Optional description for the credits section */
  creditsDescription?: string;
  /** Submit button label (e.g. "Save", "Next") */
  submitLabel: string;
  /** Cancel/back button label. Omit to render only the submit button (e.g. edit page). */
  cancelLabel?: string;
  /** Cancel/back button handler. Omit when cancelLabel is not used. */
  onCancel?: MouseEventHandler;
  /** Whether submit button is disabled (e.g. !isValid || !certifyChecked) */
  submitDisabled?: boolean;
  /** Initial checked state for the certify checkbox (e.g. for backtracking). Default false */
  initialCertifyChecked?: boolean;
  /** When true, show the preferred writer (AuthorsSelect) field for sponsored submissions. Default false */
  showPreferredWriter?: boolean;
}

/**
 * SubmissionForm molecule: shared form content for submission (title, link, image,
 * description, credits, agreement). Used in ContentStep (submit flow) and in place
 * of EditSubmissionForm (edit flow). Renders form content only; parent must wrap
 * in Formik and a <form> (e.g. Formik’s <Form>) and provide SubmissionContent
 * initialValues/validationSchema/onSubmit.
 *
 * Layout: column flex with `gap` between sections (Content block, optional writer,
 * dividers, creators, agreement, actions); inner Content/Agreement blocks use smaller gap.
 */
const SubmissionForm = ({
  credits,
  removeCredit,
  saveCredit,
  canAddCredits = true,
  creditsLabel = 'Credits',
  creditsDescription,
  submitLabel,
  cancelLabel,
  onCancel,
  submitDisabled = false,
  initialCertifyChecked = false,
  showPreferredWriter = false,
}: SubmissionFormProps) => {
  const { classes } = useStyles();
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched,
    submitForm,
  } = useFormikContext<SubmissionContent>();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [certifyChecked, setCertifyChecked] = React.useState(initialCertifyChecked);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched(event.target.id);
    handleChange(event);
  };

  const handleAddImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched('contentImage');
    const file = event.target.files?.[0];
    if (file) setFieldValue('contentImage', file);
  };

  const handleRemoveImage = () => {
    setFieldValue('contentImage', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCertifyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCertifyChecked(event.target.checked);
  };

  return (
    <Stack component="div" className={classes.root} data-component="SubmissionForm">
      <Stack className={classes.contentSection}>
        <SectionHeader
          title="Content"
          titleVariant="h2"
          divider={false}
          description="Please provide a link, title and description of your content."
        />

        <TextField
          id="contentTitle"
          label="Submission Title"
          fullWidth
          required
          placeholder="Artist Name - Song Title (Ft. Artist)"
          value={values.contentTitle}
          onChange={handleTextFieldChange}
          error={touched.contentTitle && Boolean(errors.contentTitle)}
          helperText={touched.contentTitle ? errors.contentTitle : ''}
          disableUnderline
        />

        <TextField
          id="contentUrl"
          label="Content Link"
          fullWidth
          required
          placeholder="https://"
          value={values.contentUrl}
          onChange={handleTextFieldChange}
          error={touched.contentUrl && Boolean(errors.contentUrl)}
          helperText={
            touched.contentUrl && Boolean(errors.contentUrl)
              ? errors.contentUrl
              : 'Private links will be declined.'
          }
          disableUnderline
        />

        <Box>
          <Typography variant="caption" className={classes.imageLabel} display="block">
            Featured Image
          </Typography>
          <ImageUploader
            variant="content"
            error={touched.contentImage && Boolean(errors.contentImage)}
            helperText={
              touched.contentImage
                ? (errors.contentImage as string)
                : 'Please provide 1080x1080px artwork for YouTube uploads.'
            }
            inputProps={{
              id: 'contentImage',
              ref: fileInputRef,
              onChange: handleAddImage,
            }}
            onRemoveImage={handleRemoveImage}
            file={values.contentImage}
            renderLabel={false}
          />
        </Box>

        <TextField
          id="contentDescription"
          label="Description"
          placeholder="Content description"
          multiline
          minRows={10}
          fullWidth
          required
          value={values.contentDescription}
          onChange={handleTextFieldChange}
          slotProps={{ htmlInput: { maxLength: MAX_DESCRIPTION_CHAR_COUNT } }}
          disableUnderline
          error={touched.contentDescription && Boolean(errors.contentDescription)}
          helperText={
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              component="span"
            >
              <Typography
                variant="caption"
                color={
                  touched.contentDescription && Boolean(errors.contentDescription)
                    ? 'error'
                    : 'textSecondary'
                }
                className={classes.contentDescriptionHelperText}
              >
                {touched.contentDescription && Boolean(errors.contentDescription)
                  ? errors.contentDescription
                  : 'Tell us about your content.'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {values.contentDescription.length} / {MAX_DESCRIPTION_CHAR_COUNT}
              </Typography>
            </Box>
          }
        />
      </Stack>

      {showPreferredWriter && (
        <Box sx={{ mt: 2 }}>
          <AuthorsSelect
            onChange={(preferredUsername: string) =>
              setFieldValue('preferredWriter', preferredUsername)
            }
            value={values.preferredWriter}
          />
        </Box>
      )}

      <Divider className={classes.divider} />

      <CreatorsForm
        credits={credits}
        removeCredit={removeCredit}
        saveCredit={saveCredit}
        label={creditsLabel}
        description={creditsDescription}
        canAddCredits={canAddCredits}
      />

      <Divider className={classes.divider} />

      <Stack className={classes.agreementSection}>
        <SectionHeader title="Agreement" titleVariant="h2" divider={false} />
        <FormControlLabel
          labelPlacement="start"
          control={
            <Checkbox
              color="primary"
              checked={certifyChecked}
              onChange={handleCertifyChange}
              name="certify"
              required
            />
          }
          label={
            <Typography variant="body2" className={classes.formControlLabel}>
              I certify that I have the expressed permission or authority to submit this
              content.
            </Typography>
          }
        />
      </Stack>

      <ActionButtons
        submit={{
          label: submitLabel,
          onClick: submitForm,
          disabled: submitDisabled || !certifyChecked,
        }}
        cancel={
          cancelLabel != null && onCancel != null
            ? { label: cancelLabel, onClick: onCancel }
            : undefined
        }
      />
    </Stack>
  );
};

export default SubmissionForm;
